import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookieConsent } from '@/contexts/CookieConsentContext';
import { captureAdParams, getAdParams, readCookie } from '@/lib/adParams';
import { supabase } from '@/integrations/supabase/client';

export const META_PIXEL_ID = '1359270892855307';

declare global {
  interface Window {
    fbq: any;
    _fbq: any;
  }
}

/** Optionele testcode uit de URL (?test_event_code=TESTxxxxx) voor Meta Gebeurtenissen testen. */
export function getTestEventCode(): string | undefined {
  if (typeof window === 'undefined') return undefined;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('test_event_code');
    if (fromUrl) sessionStorage.setItem('nc-meta-test-code', fromUrl);
    return fromUrl ?? sessionStorage.getItem('nc-meta-test-code') ?? undefined;
  } catch {
    return undefined;
  }
}

let pixelInitialised = false;

/** Laadt fbevents.js één keer en initialiseert de pixel. Tracking blijft uit tot toestemming. */
function loadPixel() {
  if (pixelInitialised) return;

  /* eslint-disable */
  (function (f: any, b: Document, e: string, v: string, n?: any, t?: any, s?: any) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
  /* eslint-enable */

  // Eerst init, daarna pas de consent-status: andersom verwerkt Meta de init niet.
  window.fbq('init', META_PIXEL_ID);
  pixelInitialised = true;
}

const MetaPixel = () => {
  const location = useLocation();
  const { marketing } = useCookieConsent();
  const granted = marketing === 'granted';
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    captureAdParams();
  }, []);

  // Pixel laadt (en stuurt events) pas na expliciete marketingtoestemming.
  useEffect(() => {
    if (!granted) return;
    loadPixel();
    window.fbq('consent', 'grant');
  }, [granted]);

  useEffect(() => {
    if (!granted || !window.fbq) return;
    if (lastPath.current === location.pathname) return;
    lastPath.current = location.pathname;
    trackMetaEvent('PageView');
  }, [granted, location.pathname]);

  return null;
};

/** Uniek event-ID zodat browser- en server-event niet dubbel geteld worden. */
export function newEventId() {
  return `nc-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function trackMetaEvent(
  eventName: string,
  params?: Record<string, any>,
  eventId?: string,
) {
  if (!window.fbq) return;
  const testCode = getTestEventCode();
  const options: Record<string, string> = {};
  if (eventId) options.eventID = eventId;
  if (testCode) options.test_event_code = testCode;
  window.fbq('track', eventName, params ?? {}, Object.keys(options).length ? options : undefined);
}

export function trackLead(params?: { content_name?: string; value?: number; currency?: string }) {
  trackMetaEvent('Lead', params);
}

export function trackContact() {
  trackMetaEvent('Contact');
}

interface ServerEventInput {
  eventName: string;
  eventId: string;
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
  value?: number;
  currency?: string;
}

/** Stuurt hetzelfde event ook server-side (Conversions API) door. */
export async function sendServerEvent(input: ServerEventInput) {
  try {
    await supabase.functions.invoke('meta-conversions', {
      body: {
        ...input,
        eventSourceUrl: window.location.href,
        fbc: readCookie('_fbc'),
        fbp: readCookie('_fbp'),
        adParams: getAdParams(),
        testEventCode: getTestEventCode(),
      },
    });
  } catch (err) {
    console.error('Server-side conversie niet verstuurd:', err);
  }
}

export default MetaPixel;
